import React, { useRef, useState } from 'react';
import { Button, message, Spin } from 'antd';
import { UploadOutlined, PictureOutlined } from '@ant-design/icons';
import { p1113Api } from '../../../services/p1113Api';
import type { F113 } from '../../../services/p1113Api';

interface Props {
  isEditing: boolean;
  imageUrl: string;
  setField: (f: keyof F113, v: unknown) => void;
}

const ACCEPTED_FORMATS = '.bmp,.png,.jpg,.jpeg';

const Tab3Image: React.FC<Props> = React.memo(({ isEditing, imageUrl, setField }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      message.error('檔案大小超過 10MB 限制');
      return;
    }

    setUploading(true);
    try {
      const res = await p1113Api.uploadImage(file);
      setField('f113_18', res.url);
      message.success('圖片上傳成功');
    } catch {
      message.error('圖片上傳失敗');
    } finally {
      setUploading(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p1113-image-tab">
      <div className="p1113-image-preview">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="產品圖片"
            className="p1113-product-image"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        ) : (
          <div className="p1113-image-placeholder">
            <PictureOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
            <p>尚無圖片</p>
          </div>
        )}
      </div>

      {/* BR-31: Upload button only in edit mode (BR-25/26: CMDPIC enabled only in edit) */}
      <div className="p1113-image-actions">
        <Spin spinning={uploading}>
          <Button
            icon={<UploadOutlined />}
            onClick={handleUploadClick}
            disabled={!isEditing}
            type="primary"
          >
            {imageUrl ? '更換圖片' : '新增圖片'}
          </Button>
        </Spin>
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_FORMATS}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>

      {imageUrl && (
        <div className="p1113-image-url">
          <span className="p1113-text-muted">URL: {imageUrl}</span>
        </div>
      )}
    </div>
  );
});

Tab3Image.displayName = 'Tab3Image';
export default Tab3Image;
